import { useEffect } from "react";
import type { TableProps } from "antd";
import { Button, Modal, Space, Table } from "antd";
import type { ApplicationItem } from "@/types/Application/application";
import { RejectDetailsApplicationModal } from "./RejectDetailsApplicationModal";
import { EditApplicationModal } from "./EditApplicationModal";
import { DeleteApplicationModal } from "./DeleteApplicationModal";
import { AcceptApplicationModal } from "./AcceptApplicationModal";
import { formatValue } from "@/utils/formatValue";
import { renderNameCell } from "@/features/admin/hook/renderNameCell";
import { useApplicationModal } from "./hook/useApplicationModal";
import { useVillageList } from "./hook/useVillageList";
import { safeFormatyyyyMMddDateTime } from "@/utils/formatDate";

interface RejectApplicationTable {
  applications: ApplicationItem[];
  loading: boolean;
  totalCount?: number;
  currentPage?: number;
  pageSize?: number;
  resetKey?: number;
  onPageChange?: (page: number) => void;
  onRefresh?: () => void;
}

export function RejectApplicationTable({
  applications,
  loading,
  totalCount,
  currentPage,
  pageSize,
  resetKey = 0,
  onPageChange,
  onRefresh,
}: RejectApplicationTable) {
  const { fetchVillageList, villageList } = useVillageList();

  const {
    selectedRow,
    detailData,
    resultType,

    isDetailModalOpen,
    isEditModalOpen,
    isDeleteModalOpen,
    isAcceptModalOpen,
    resultModalOpen,
    handleOpenModal,
    handleCloseModal,

    handleOpenEditModal,
    handleCloseEditModal,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleBackFromDeleteModal,
    handleDeleteSuccess,
    handleBackToDetailModal,
    handleCloseAcceptModal,
    handleBackFromAcceptModal,
    handleAcceptSuccess,
    handleCloseResultModal,
  } = useApplicationModal();

  useEffect(() => {
    fetchVillageList();
  }, []);

  const columns: TableProps<ApplicationItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "registrationCode",
      sorter: (a, b) =>
        a.registrationCode.localeCompare(b.registrationCode, undefined, {
          numeric: true,
        }),
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (value, record) => (
        <span
          className="block cursor-pointer"
          onClick={() => handleOpenModal(record)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleOpenModal(record);
            }
          }}
          role="button"
          tabIndex={0}
        >
          {renderNameCell(value)}
        </span>
      ),
    },
    {
      title: "MyKad/Passport",
      dataIndex: "identityNo",
      sorter: (a, b) => a.identityNo.localeCompare(b.identityNo),
      render: (value) => formatValue(value),
    },
    {
      title: "Jantina",
      dataIndex: "gender",
      filters: [
        { text: "Lelaki", value: "LELAKI" },
        { text: "Perempuan", value: "PEREMPUAN" },
      ],
      onFilter: (value, record) => record.gender === String(value),
    },
    {
      title: "Kategori",
      dataIndex: "residencyStatus",
      sorter: (a, b) => a.residencyStatus.localeCompare(b.residencyStatus),
      render: (value) => formatValue(value),
    },
    {
      title: "Kampung",
      dataIndex: "villageName",
      sorter: (a, b) => a.villageName.localeCompare(b.villageName),
      render: (value) => formatValue(value),
    },
     {
          title: "Tarikh Daftar",
          dataIndex: "submittedOn",
          sorter: (a, b) => {
            const aDate = new Date(a.submittedOn).getTime();
            const bDate = new Date(b.submittedOn).getTime();
    
            return aDate - bDate;
          },
          render: (value) => safeFormatyyyyMMddDateTime(value),
        },
    {
      title: "Tindakan",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            className="px-4 rounded-lg"
            onClick={() => handleOpenModal(record)}
          >
            Lihat
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
        <Table<ApplicationItem>
          key={resetKey}
          size="middle"
          showHeader
          tableLayout="fixed"
          className="c-table"
          columns={columns}
          dataSource={applications}
          loading={loading}
          rowKey="registrationId"
          scroll={{ x: "max-content" }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalCount,
            onChange: onPageChange,
            disabled: loading,
            showSizeChanger: false,
            showTotal: (total, range) =>
              `Memaparkan ${range[0]}-${range[1]} daripada ${total}`,
          }}
        />
      </div>

      <RejectDetailsApplicationModal
        open={isDetailModalOpen}
        selectedRow={selectedRow}
        detailData={detailData}
        onClose={handleCloseModal}
        onEdit={handleOpenEditModal}
      />

      <DeleteApplicationModal
        open={isDeleteModalOpen}
        registrationId={selectedRow?.registrationId ?? detailData?.registrationId ?? ""}
        name={selectedRow?.name ?? detailData?.name ?? "-"}
        selectedRow={selectedRow}
        detailData={detailData}
        onClose={handleCloseDeleteModal}
        onBack={handleBackFromDeleteModal}
        onConfirm={handleDeleteSuccess}
        onSuccess={onRefresh}
      />

      <EditApplicationModal
        open={isEditModalOpen}
        application={detailData}
        villageList={villageList ?? []}
        onClose={handleCloseEditModal}
        onBack={handleBackToDetailModal}
        onDelete={handleOpenDeleteModal}
        onSuccess={onRefresh}
      />

      <AcceptApplicationModal
        open={isAcceptModalOpen}
        registrationId={
          selectedRow?.registrationId ?? detailData?.registrationId ?? ""
        }
        name={selectedRow?.name ?? detailData?.name ?? "-"}
        onClose={handleCloseAcceptModal}
        onBack={handleBackFromAcceptModal}
        onConfirm={handleAcceptSuccess}
        onSuccess={onRefresh}
      />

      <Modal
        open={resultModalOpen}
        onCancel={handleCloseResultModal}
        footer={false}
        closable={false}
        centered
      >
        <div className="text-center">
          <div className="mb-4 text-[#1F993B] text-4xl font-bold">✓</div>
          <div className="text-xl font-bold text-[#272728]">
            {resultType === "accepted"
              ? "Permohonan berjaya diterima"
              : resultType === "deleted"
                ? "Permohonan berjaya dipadam"
                : "Permohonan telah ditolak"}
          </div>
        </div>
      </Modal>
    </>
  );
}
